<template>
    <section class="relative hero pt-16 lg:pt-[100px] pb-48 lg:pb-[380px] text-white overflow-hidden" :style="{ background: backgroundColor }">
        <img v-if="backgroundImage" class="z-0 absolute left-0 hidden lg:block w-full" :src="$asset(backgroundImage, 'public')" />
        <img v-if="backgroundImage" class="z-0 absolute bottom-[160px] left-0 scale-[2.5] block lg:hidden w-full" :src="$asset(backgroundImage, 'public')" />

        <div class="relative z-10 mx-auto px-5 lg:px-[120px] text-center">
            <!-- Badge-Reihe -->
            <div v-if="badges && badges.length" class="flex justify-center gap-5 mb-8">
                <img
                    v-for="(badge, index) in badges"
                    :key="index"
                    :alt="badge.alt"
                    class="h-full object-cover max-w-[5rem] max-h-[5rem]"
                    :class="badge.class"
                    :src="$asset(badge.src, 'public')"
                />
            </div>

            <!-- H1 -->
            <h1 class="text-[32px] leading-[42px] md:text-5xl lg:text-[56px] lg:leading-[73px] font-semibold mb-6 max-w-[1200px] mx-auto">
                <span v-html="renderTitle()"></span>
            </h1>

            <!-- Subheadline -->
            <p class="text-[20px] leading-[26px] font-semibold mb-10 max-w-[1200px] mx-auto text-white" v-html="subtitle"></p>

            <!-- 3 Benefit-Punkte -->
            <div v-if="benefits && benefits.length" class="max-w-[1200px] mx-auto mb-10 flex md:flex-row flex-col gap-[20px] justify-center">
                <div v-for="(benefit, index) in benefits" :key="index" class="flex items-center gap-3 text-left lg:w-fit w-full">
                    <svg class="w-6 h-6 flex-shrink-0" :class="checkmarkColor" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <span class="text-[18px] font-pro leading-[23px]" v-html="benefit"></span>
                </div>
            </div>

            <!-- CTA-Reihe -->
            <div class="flex flex-col md:flex-row justify-center items-center gap-6 mb-12 h-[64px]">
                <router-link
                    v-if="showCta"
                    :to="ctaLink"
                    class="p-[20px] leading-tight lg:leading-relaxed rounded-full font-semibold text-[20px] inline-flex lg:w-fit w-full justify-center items-center gap-2 transition text-white hover:opacity-90"
                    :style="{ background: `linear-gradient(to bottom right, ${buttonGradientFrom}, ${buttonGradientTo})` }"
                >
                    {{ ctaText }}
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                </router-link>
                <div v-else class="text-white px-2 lg:px-6 py-3 rounded-full font-semibold transition text-xs lg:text-md" :style="{ backgroundColor: fallbackCtaBackgroundColor }">
                    {{ fallbackCtaText }}
                </div>
                <div v-if="priceLabel && priceAmount" class="text-left">
                    <div class="text-md font-bold text-center lg:text-left">{{ priceLabel }}</div>
                    <div class="text-xl font-bold" :class="priceColor">{{ priceAmount }}</div>
                </div>
            </div>

            <!-- Social-Proof-Reihe -->
            <div v-if="socialProofText || appStoreData || googlePlayData" class="flex flex-wrap justify-center items-center gap-8 max-w-4xl mx-auto">
                <!-- Avatar-Gruppe -->
                <div v-if="socialProofText && socialProofImage" class="flex items-center gap-3">
                    <div class="flex -space-x-2">
                        <img :src="$asset(socialProofImage)" />
                    </div>
                    <div class="text-left text-sm" v-html="socialProofText"></div>
                </div>

                <!-- App Store -->
                <div v-if="appStoreData" class="text-center">
                    <div class="w-32 h-10 mb-2">
                        <a :href="appStoreData.link" class="h-auto" target="_blank">
                            <img alt="Apple Store" class="object-contain max-h-[50px]" :src="$asset(appStoreData.badge, 'public')" />
                        </a>
                    </div>
                    <div class="flex items-center justify-center gap-2 text-sm">
                        <span class="font-bold">{{ appStoreData.rating }}</span>
                        <img class="w-2/4 object-contain max-h-[180px] lg:w-auto" :src="$asset(appStoreData.stars, 'public')" alt="" />
                        <span>{{ appStoreData.reviews }}</span>
                    </div>
                </div>

                <!-- Google Play -->
                <div v-if="googlePlayData" class="text-center">
                    <div class="w-32 h-10 mb-2">
                        <a :href="googlePlayData.link" class="h-auto" target="_blank">
                            <img alt="Google Play Store" class="object-contain max-h-[50px]" :src="$asset(googlePlayData.badge, 'public')" />
                        </a>
                    </div>
                    <div class="flex items-center justify-center gap-2 text-sm">
                        <span class="font-bold">{{ googlePlayData.rating }}</span>
                        <img class="w-2/4 object-contain max-h-[180px] lg:w-auto" :src="$asset(googlePlayData.stars, 'public')" alt="" />

                        <span>{{ googlePlayData.reviews }}</span>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import i18next from 'i18next';

const props = defineProps({
    backgroundColor: {
        type: String,
        default: 'linear-gradient(to bottom right, #1E3A8A, #1E3A8A, #1E40AF)'
    },
    badges: {
        type: Array,
        default: () => []
    },
    title: {
        type: String,
        default: ''
    },
    titleHighlight: {
        type: [String, Array],
        default: ''
    },
    titleHighlightGradientFrom: {
        type: String,
        default: '#11D2D4'
    },
    titleHighlightGradientTo: {
        type: String,
        default: '#0C7475'
    },
    subtitle: {
        type: String,
        default: ''
    },
    benefits: {
        type: Array,
        default: () => []
    },
    checkmarkColor: {
        type: String,
        default: 'text-blue'
    },
    ctaText: {
        type: String,
        default: 'Jetzt starten'
    },
    ctaLink: {
        type: String,
        default: '/'
    },
    showCta: {
        type: Boolean,
        default: true
    },
    priceLabel: {
        type: String,
        default: ''
    },
    priceAmount: {
        type: String,
        default: ''
    },
    priceColor: {
        type: String,
        default: 'text-blue'
    },
    socialProofText: {
        type: String,
        default: ''
    },
    socialProofImage: {
        type: String,
        default: ''
    },
    appStoreData: {
        type: Object,
        default: null
    },
    googlePlayData: {
        type: Object,
        default: null
    },
    backgroundImage: {
        type: String,
        default: ''
    },
    buttonGradientFrom: {
        type: String,
        default: '#11D2D4'
    },
    buttonGradientTo: {
        type: String,
        default: '#0C7475'
    },
    fallbackCtaText: {
        type: String,
        default: ''
    },
    fallbackCtaBackgroundColor: {
        type: String,
        default: '#1E40AF'
    }
});

const renderTitle = () => {
    if (!props.titleHighlight || (Array.isArray(props.titleHighlight) && props.titleHighlight.length === 0)) return props.title;

    const highlights = Array.isArray(props.titleHighlight) ? props.titleHighlight : [props.titleHighlight];
    const highlightRegex = new RegExp(`(${highlights.map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');

    return props.title.replace(
        highlightRegex,
        `<span class="bg-gradient-to-t text-transparent bg-clip-text" style="background: linear-gradient(to top, ${props.titleHighlightGradientFrom}, ${props.titleHighlightGradientTo}); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">$1</span>`
    );
};
</script>
